import { useState } from "react";
import AccountLayout from "@/components/feature/AccountLayout";
import { useAuth } from "@/hooks/useAuth";

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { user } = useAuth();

  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState({
    orderUpdates: true,
    promotions: false,
    newsletter: true,
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <AccountLayout activeTab="settings">
      <div className="max-w-2xl">
        <p className="text-sm text-foreground-500 mb-8">{t("account.settings_subtitle")}</p>

        <form onSubmit={handleSave} className="space-y-10">
          {/* Profile Information */}
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
              {t("account.profile_info")}
            </h2>
            <div className="space-y-5">
              {/* Avatar + Name */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.full_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl font-bold text-primary-600">
                      {(fullName || "U").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">
                    {fullName || user?.full_name || "User"}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {user?.country_code} {user?.phone}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t("account.full_name_label")}
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t("account.phone_label")}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t("account.email_label")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("account.email_placeholder")}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
              {t("account.preferences")}
            </h2>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t("account.language_pref")}
                </label>
                <select
                  value={isAr ? "ar" : "en"}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                >
                  <option value="en">{t("language.en")}</option>
                  <option value="ar">{t("language.ar")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t("account.currency")}
                </label>
                <select
                  defaultValue="USD"
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EGP">EGP (E£)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="SAR">SAR (﷼)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-3">
                  {t("account.email_notifications")}
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      onClick={() =>
                        setEmailNotifications((prev) => ({
                          ...prev,
                          orderUpdates: !prev.orderUpdates,
                        }))
                      }
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        emailNotifications.orderUpdates
                          ? "bg-primary-500 border-primary-500"
                          : "border-background-300"
                      }`}
                    >
                      {emailNotifications.orderUpdates && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <span className="text-sm text-foreground-700">{t("account.order_updates")}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      onClick={() =>
                        setEmailNotifications((prev) => ({
                          ...prev,
                          promotions: !prev.promotions,
                        }))
                      }
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        emailNotifications.promotions
                          ? "bg-primary-500 border-primary-500"
                          : "border-background-300"
                      }`}
                    >
                      {emailNotifications.promotions && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <span className="text-sm text-foreground-700">{t("account.promotions")}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      onClick={() =>
                        setEmailNotifications((prev) => ({
                          ...prev,
                          newsletter: !prev.newsletter,
                        }))
                      }
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        emailNotifications.newsletter
                          ? "bg-primary-500 border-primary-500"
                          : "border-background-300"
                      }`}
                    >
                      {emailNotifications.newsletter && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <span className="text-sm text-foreground-700">{t("account.newsletter")}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer text-sm whitespace-nowrap"
            >
              {t("account.save_changes")}
            </button>
            {saved && (
              <span className="text-sm font-medium text-secondary-600 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line"></i>
                {t("account.saved")}
              </span>
            )}
          </div>
        </form>

        {/* Danger Zone */}
        <div className="mt-16 pt-8 border-t border-background-200">
          <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
            {t("account.danger_zone")}
          </h2>
          <p className="text-sm text-foreground-500 mb-5">{t("account.delete_warning")}</p>
          <button
            onClick={() => {
              if (window.confirm(t("account.delete_warning"))) {
                // Mock delete
              }
            }}
            className="px-6 py-3 border-2 border-red-200 text-red-600 font-semibold rounded-full hover:bg-red-50 transition-colors cursor-pointer text-sm whitespace-nowrap"
          >
            {t("account.delete_button")}
          </button>
        </div>
      </div>
    </AccountLayout>
  );
}